import React, { useEffect, useMemo, useState } from "react";
import {
  CardElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { toast } from "sonner";
import {
  AlertCircle,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Loader2,
  ShieldOff,
  XCircle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatUSD } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StripeElementsProvider } from "@/components/StripeElementsProvider";

const chargeStatusPill = (status) => {
  const map = {
    pending_charge: { label: "Pending charge", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    processing: { label: "Processing", cls: "bg-sky-50 text-sky-700 border-sky-200" },
    succeeded: { label: "Charged", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    failed: { label: "Failed", cls: "bg-rose-50 text-rose-700 border-rose-200" },
    refunded: { label: "Refunded", cls: "bg-slate-100 text-slate-600 border-slate-200" },
    manually_waived: { label: "Waived", cls: "bg-purple-50 text-purple-700 border-purple-200" },
    not_billable: { label: "Not billable", cls: "bg-slate-50 text-slate-500 border-slate-200" },
  };
  const m = map[status] || map.pending_charge;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}>
      {m.label}
    </span>
  );
};

// Inner form — must live INSIDE <Elements> so it can useStripe / useElements.
const AddCardForm = ({ clientSecret, dealerName, dealerEmail, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    const cardEl = elements.getElement(CardElement);
    const { error: stripeErr, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardEl,
        billing_details: { name: dealerName, email: dealerEmail },
      },
    });
    if (stripeErr) {
      setError(stripeErr.message || "Card setup failed");
      setSubmitting(false);
      return;
    }
    // Attach + persist server-side
    try {
      await api.post("/dealer/billing/setup-intent/confirm", { payment_method_id: setupIntent.payment_method });
      toast.success("Payment method saved");
      onSuccess?.();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="dealer-add-card-form">
      <div className="border border-slate-200 rounded-lg p-3 bg-white">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "15px",
                color: "#0f172a",
                fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
                "::placeholder": { color: "#94a3b8" },
              },
              invalid: { color: "#dc2626" },
            },
            hidePostalCode: false,
          }}
        />
      </div>
      {error && (
        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="dealer-card-error">
          {error}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>Cancel</Button>
        <Button type="submit" className="bg-brand hover:bg-brand-hover text-white" disabled={!stripe || submitting} data-testid="dealer-save-card-btn">
          {submitting && <Loader2 size={16} className="animate-spin mr-1" />} Save payment method
        </Button>
      </div>
      <p className="text-xs text-slate-500 pt-1">Test mode — use <code className="bg-slate-100 px-1 py-0.5 rounded">4242 4242 4242 4242</code>, any future expiry, any CVC.</p>
    </form>
  );
};

const DealerBilling = () => {
  const [status, setStatus] = useState(null);
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/dealer/billing/status"),
        api.get("/dealer/billing/charges"),
      ]);
      setStatus(s.data);
      setCharges(c.data.rows || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const openAddCard = async () => {
    try {
      const { data } = await api.post("/dealer/billing/setup-intent");
      setClientSecret(data.client_secret);
      setAddOpen(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeCard = async () => {
    if (!window.confirm("Remove your payment method? You'll need to add one before you can be charged again.")) return;
    try {
      await api.post("/dealer/billing/payment-method/remove");
      toast.success("Payment method removed");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openReceipt = async (payment_intent_id) => {
    if (!payment_intent_id) return;
    try {
      const { data } = await api.get(`/dealer/billing/receipts/${payment_intent_id}`);
      if (data.receipt_url) {
        window.open(data.receipt_url, "_blank", "noopener");
      } else {
        toast("No receipt available yet — try again in a moment.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const hasCard = status?.has_payment_method;
  const hasFailed = (status?.failed_charges_this_month || 0) > 0;

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6" data-testid="dealer-billing-page">
      <div>
        <h1 className="font-display text-3xl text-slate-900">Billing</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your payment method and see every $300 charge per showed-up appointment.</p>
      </div>

      {hasFailed && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 flex items-start gap-3" data-testid="dealer-billing-failed-banner">
          <AlertCircle size={18} className="text-rose-600 shrink-0 mt-0.5" />
          <div className="text-sm text-rose-800 flex-1">
            <p className="font-semibold">{status.failed_charges_this_month} charge{status.failed_charges_this_month !== 1 ? "s" : ""} failed this month.</p>
            <p className="mt-1">Update your payment method below, then contact <a href="mailto:support@belomotors.com" className="underline">support@belomotors.com</a> to retry these charges.</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="h-40 bg-slate-100 rounded-2xl animate-pulse" />
      ) : (
        <>
          {/* Payment method card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="dealer-payment-method-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Payment method</p>
                {hasCard ? (
                  <div className="mt-2 flex items-center gap-3">
                    <div className="w-12 h-8 rounded-md bg-slate-900 text-white text-[10px] font-bold uppercase flex items-center justify-center tracking-wider">
                      {status.payment_method_brand}
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-slate-900" data-testid="dealer-card-summary">
                        {status.payment_method_brand?.toUpperCase()} •••• {status.payment_method_last4}
                      </p>
                      <p className="text-xs text-emerald-700 flex items-center gap-1 mt-0.5">
                        <CheckCircle2 size={12} /> On file and ready
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2">
                    <p className="text-lg font-semibold text-rose-700">No payment method on file</p>
                    <p className="text-xs text-slate-500 mt-1">You must add one before you can receive appointment leads.</p>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {hasCard ? (
                  <>
                    <Button variant="outline" onClick={openAddCard} data-testid="dealer-update-card-btn"><CreditCard size={14} /> Update</Button>
                    <Button variant="outline" className="text-rose-600 hover:text-rose-700" onClick={removeCard} data-testid="dealer-remove-card-btn"><ShieldOff size={14} /> Remove</Button>
                  </>
                ) : (
                  <Button className="bg-gold hover:bg-gold text-white" onClick={openAddCard} data-testid="dealer-add-card-btn"><CreditCard size={16} /> Add payment method</Button>
                )}
              </div>
            </div>
          </div>

          {/* Current month summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="dealer-billing-pending-card">
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Pending this month</p>
              <p className="font-display text-3xl text-slate-900 mt-2">{formatUSD((status?.pending_amount_cents || 0) / 100, 0)}</p>
              <p className="text-xs text-slate-500 mt-1">{status?.pending_charges_this_month || 0} appointment{status?.pending_charges_this_month === 1 ? "" : "s"} awaiting charge</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="dealer-billing-succeeded-card">
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Charged this month</p>
              <p className="font-display text-3xl text-emerald-700 mt-2">{formatUSD((status?.succeeded_amount_cents || 0) / 100, 0)}</p>
              <p className="text-xs text-slate-500 mt-1">{status?.succeeded_charges_this_month || 0} successful</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="dealer-billing-failed-card">
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Failed this month</p>
              <p className={`font-display text-3xl mt-2 ${hasFailed ? "text-rose-700" : "text-slate-400"}`}>{formatUSD((status?.failed_amount_cents || 0) / 100, 0)}</p>
              <p className="text-xs text-slate-500 mt-1">{status?.failed_charges_this_month || 0} needs attention</p>
            </div>
          </div>

          {/* Charges table */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="dealer-charges-table">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Current-month charges</p>
                <p className="text-sm text-slate-700 font-medium mt-0.5">{status?.month_label}</p>
              </div>
            </div>
            {charges.length === 0 ? (
              <div className="p-8 text-sm text-slate-500 text-center">No showed-up appointments this month yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Date</th>
                    <th className="text-left px-4 py-3 font-semibold">Customer</th>
                    <th className="text-left px-4 py-3 font-semibold">Vehicle</th>
                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                    <th className="text-right px-4 py-3 font-semibold">Amount</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {charges.map((row) => (
                    <tr key={row.appointment_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`dealer-charge-row-${row.appointment_id}`}>
                      <td className="px-4 py-3 text-slate-600">{row.showed_up_at ? new Date(row.showed_up_at).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3 text-slate-800">{row.customer_name}</td>
                      <td className="px-4 py-3 text-slate-800">{row.vehicle_summary}</td>
                      <td className="px-4 py-3">
                        {chargeStatusPill(row.charge_status)}
                        {row.charge_status === "failed" && row.charge_error && (
                          <div className="text-[11px] text-rose-600 mt-1 max-w-[220px] truncate" title={row.charge_error}>{row.charge_error}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">{formatUSD(row.amount_cents / 100, 2)}</td>
                      <td className="px-4 py-3 text-right">
                        {row.stripe_payment_intent_id && row.charge_status === "succeeded" ? (
                          <button className="text-brand text-xs font-medium inline-flex items-center gap-1 hover:underline" onClick={() => openReceipt(row.stripe_payment_intent_id)} data-testid={`dealer-receipt-btn-${row.appointment_id}`}>
                            Receipt <ExternalLink size={11} />
                          </button>
                        ) : row.charge_status === "failed" ? (
                          <span className="text-xs text-slate-400">Contact support</span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Past 90d */}
          {status?.past_charges?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="dealer-past-charges">
              <div className="px-5 py-3 border-b border-slate-100">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Past 90 days</p>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Date</th>
                    <th className="text-left px-4 py-3 font-semibold">Customer</th>
                    <th className="text-left px-4 py-3 font-semibold">Vehicle</th>
                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                    <th className="text-right px-4 py-3 font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {status.past_charges.map((row) => (
                    <tr key={row.appointment_id} className="border-t border-slate-100">
                      <td className="px-4 py-3 text-slate-600">{row.showed_up_at ? new Date(row.showed_up_at).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3 text-slate-800">{row.customer_name}</td>
                      <td className="px-4 py-3 text-slate-800">{row.vehicle_summary}</td>
                      <td className="px-4 py-3">{chargeStatusPill(row.charge_status)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{formatUSD(row.amount_cents / 100, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Add-card modal */}
      <Dialog open={addOpen} onOpenChange={(v) => { setAddOpen(v); if (!v) setClientSecret(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="dealer-add-card-dialog">
          <DialogHeader>
            <DialogTitle>{hasCard ? "Update payment method" : "Add payment method"}</DialogTitle>
            <DialogDescription>
              Belomotors uses Stripe for secure card storage. Your card is charged $300 only when a customer shows up for an appointment.
            </DialogDescription>
          </DialogHeader>
          {clientSecret ? (
            <StripeElementsProvider clientSecret={clientSecret}>
              <AddCardForm
                clientSecret={clientSecret}
                dealerName="Belomotors Dealer"
                dealerEmail={status?.billing_email}
                onSuccess={() => { setAddOpen(false); setClientSecret(null); reload(); }}
                onCancel={() => { setAddOpen(false); setClientSecret(null); }}
              />
            </StripeElementsProvider>
          ) : (
            <div className="py-10 flex items-center justify-center text-slate-400"><Loader2 size={18} className="animate-spin" /></div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DealerBilling;
